import { BaseTableRow, getHeaderName, TableProps } from "@/types";
import { GridActionsCellItem, GridRowParams } from "@mui/x-data-grid-pro";
import { SyntheticEvent } from "react";
import { MemberSelectionTypeOption, SettingsGroup } from "../../data/settings/SettingsGroup";
import MoreVertIcon from "../../resources/images/icons-more-vertical.svg";
import { highlightKeyword } from "../../utilities";
import { Table } from "./Table";

interface GroupsTableRow extends BaseTableRow {
  memberSelectionType?: MemberSelectionTypeOption;
  name?: string;
  numberOfManagers?: number;
  numberOfEmployees?: number;
}

interface GroupsTableProps<T extends GroupsTableRow = GroupsTableRow> extends TableProps<T> {
  onGroupSelect?: (e: SyntheticEvent, row: T) => void;
}

export class GroupsTable<T extends GroupsTableRow = GroupsTableRow> extends Table<
  T,
  GroupsTableProps<T>
> {
  static columns = {
    name: {
      field: "name",
      label: "tableColumns.group"
    },
    memberCount: {
      field: "memberCount",
      label: "tableColumns.employees"
    },
    managerCount: {
      field: "managerCount",
      label: "tableColumns.access"
    },
    actions: {
      field: "actions",
      label: "tableColumns.actions"
    }
  };

  static defaultProps = {
    ...Table.defaultProps,
    columns: [{ type: GroupsTable.columns.name }, { type: GroupsTable.columns.actions }],
    noRowsMessage: "Click the New group button to add a group",
    rowHasAction: undefined
  };

  constructor(props: GroupsTableProps<T>) {
    super(props);

    this.state = {
      ...this.state,
      sortModel: this.props.sortModel
        ? this.props.sortModel
        : [
            {
              field: "name",
              sort: "asc"
            }
          ]
    };
  }

  getRowId(g: T) {
    return this.tableId + "_" + g.groupId;
  }

  onRowClick(params: GridRowParams<T>, e: SyntheticEvent) {
    const { onGroupSelect } = this.props;
    if (onGroupSelect) onGroupSelect(e, params.row);
  }

  initColumns() {
    const { columns, sortable, disabled, keyword } = this.props;

    this.columns = [];

    if (!columns) return;

    columns.forEach((c) => {
      if (this.hasColumn(c)) {
        if (c.type === GroupsTable.columns.name)
          this.columns.push({
            field: "name",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            flex: 1,
            filterable: false,
            disableReorder: true,
            valueGetter: (_value, row) => {
              if (row.memberSelectionType === SettingsGroup.memberSelectionType.ruleBased) {
                const translatedRuleBased = this.props.translate
                  ? this.props.translate(row.memberSelectionType.translationKey)
                  : row.memberSelectionType.name;
                return row.name + " (" + translatedRuleBased.toLowerCase() + ")";
              }
              return row.name;
            },
            renderCell: (params) => highlightKeyword(params.row.name || "", keyword)
          });
      }

      if (this.hasColumn(c)) {
        if (c.type === GroupsTable.columns.memberCount)
          this.columns.push({
            field: "memberCount",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            filterable: false,
            disableReorder: true,
            width: 120,
            type: "number",
            valueGetter: (_value, row) => row.numberOfEmployees
          });
      }

      if (this.hasColumn(c)) {
        if (c.type === GroupsTable.columns.managerCount)
          this.columns.push({
            field: "managerCount",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            filterable: false,
            disableReorder: true,
            width: 120,
            type: "number",
            valueGetter: (_value, row) => row.numberOfManagers
          });
      }

      if (c.type === GroupsTable.columns.actions)
        this.columns.push({
          field: "actions",
          type: "actions",
          filterable: false,
          disableReorder: true,
          align: "right",
          getActions: (params) => [
            <GridActionsCellItem
              label=''
              icon={<MoreVertIcon />}
              disabled={disabled}
              onClick={(e) => this.props.onGroupAction?.(e, params.row)}
              key={Math.random().toString(36).substring(7)}
            />
          ]
        });
    });
  }
}
